import { useState, useEffect, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ThumbsUp, ThumbsDown, Loader2, Send, Pencil, X, Check, Trash2 } from "lucide-react";
import { ConfirmDeleteDialog } from "@/components/ConfirmDeleteDialog";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const GoNoGoPanel = () => {
  const { selectedProduct } = useAuth();
  const productId = selectedProduct?.id;
  const [votes, setVotes] = useState([]);
  const [stakeholders, setStakeholders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStakeholder, setSelectedStakeholder] = useState("");
  const [vote, setVote] = useState("");
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editVote, setEditVote] = useState("");
  const [editComment, setEditComment] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const startEdit = (v) => {
    setEditingId(v.id);
    setEditVote(v.vote);
    setEditComment(v.comment || "");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditVote("");
    setEditComment("");
  };

  const saveEdit = async (vid) => {
    setSaving(true);
    try {
      await axios.patch(`${API}/gonogo/${vid}`, { vote: editVote, comment: editComment });
      cancelEdit();
      const params = productId ? { product_id: productId } : {};
      const r = await axios.get(`${API}/gonogo`, { params });
      setVotes(r.data);
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const deleteVote = async (vid) => {
    try {
      await axios.delete(`${API}/gonogo/${vid}`);
      const params = productId ? { product_id: productId } : {};
      const r = await axios.get(`${API}/gonogo`, { params });
      setVotes(r.data);
    } catch (e) { console.error(e); }
  };

  const fetchData = useCallback(async () => {
    try {
      const params = productId ? { product_id: productId } : {};
      const [vRes, sRes] = await Promise.all([axios.get(`${API}/gonogo`, { params }), axios.get(`${API}/stakeholders`, { params })]);
      setVotes(vRes.data);
      setStakeholders(sRes.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [productId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const submitVote = async () => {
    if (!selectedStakeholder || !vote) return;
    setSubmitting(true);
    const sh = stakeholders.find((s) => s.id === selectedStakeholder);
    try {
      await axios.post(`${API}/gonogo`, { stakeholder_id: selectedStakeholder, stakeholder_name: sh?.name || "Unknown", vote, comment, product_id: productId });
      setSelectedStakeholder(""); setVote(""); setComment("");
      setShowForm(false);
      const params = productId ? { product_id: productId } : {};
      const r = await axios.get(`${API}/gonogo`, { params });
      setVotes(r.data);
    } catch (e) { console.error(e); }
    setSubmitting(false);
  };

  const goCount = votes.filter((v) => v.vote === "Go").length;
  const nogoCount = votes.filter((v) => v.vote === "No Go").length;
  const goPercent = votes.length > 0 ? Math.round((goCount / votes.length) * 100) : 0;

  // Determine which stakeholders have already voted
  const votedIds = new Set(votes.map((v) => v.stakeholder_id));

  return (
    <>
    <div data-testid="gonogo-panel">
      <p className="text-xs font-semibold tracking-widest uppercase text-[#39D98A] mb-3">Decision</p>
      <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-slate-900 dark:text-slate-100 font-['Outfit']">Go / No-Go</h1>
      <p className="text-base text-slate-500 dark:text-slate-400 mt-2">Stakeholder voting on launch readiness</p>
      <Separator className="my-8 bg-slate-200 dark:bg-slate-700" />

      {loading ? <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div> : (
        <>
          {/* Summary */}
          <Card className="border border-slate-200 dark:border-slate-700 shadow-sm bg-white dark:bg-slate-800/60 mb-8" data-testid="gonogo-summary">
            <CardContent className="p-6">
              <div className="flex items-center gap-8 mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-14 h-14 rounded-xl bg-green-50 dark:bg-green-900/30 flex items-center justify-center"><ThumbsUp className="w-7 h-7 text-green-500" /></div>
                  <div><p className="text-3xl font-bold text-slate-900 dark:text-slate-100 font-['Outfit']">{goCount}</p><p className="text-xs text-slate-400 dark:text-slate-500">Go</p></div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-14 h-14 rounded-xl bg-red-50 dark:bg-red-900/30 flex items-center justify-center"><ThumbsDown className="w-7 h-7 text-red-500" /></div>
                  <div><p className="text-3xl font-bold text-slate-900 dark:text-slate-100 font-['Outfit']">{nogoCount}</p><p className="text-xs text-slate-400 dark:text-slate-500">No Go</p></div>
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1"><span className="text-xs font-medium text-slate-500 dark:text-slate-400">Approval</span><span className="text-xs font-bold text-slate-700 dark:text-slate-200">{goPercent}%</span></div>
                  <div className="h-4 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full transition-all duration-500 ${goPercent >= 50 ? "bg-green-500" : "bg-red-400"}`} style={{ width: `${goPercent || 0}%` }} />
                  </div>
                  <p className="text-[11px] text-slate-400 dark:text-slate-500 mt-1">{votes.length} of {stakeholders.length} stakeholders voted</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Vote form - shown only when showForm is true */}
          {showForm && (
          <Card className="border border-slate-200 dark:border-slate-700 shadow-sm bg-white dark:bg-slate-800/60 mb-8" data-testid="gonogo-form-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-5">
                <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100 font-['Outfit']">Cast Your Vote</h3>
                <button onClick={() => setShowForm(false)} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors" data-testid="gonogo-close-form">
                  <X className="w-5 h-5" />
                </button>
              </div>
              {stakeholders.length === 0 ? (
                <p className="text-sm text-slate-400 dark:text-slate-500">Add stakeholders first in the Stakeholders section before voting.</p>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5 block">Stakeholder</label>
                      <Select value={selectedStakeholder} onValueChange={setSelectedStakeholder}>
                        <SelectTrigger data-testid="gonogo-stakeholder-select"><SelectValue placeholder="Select stakeholder" /></SelectTrigger>
                        <SelectContent>
                          {stakeholders.map((s) => (
                            <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5 block">Vote</label>
                      <div className="flex gap-3">
                        <button onClick={() => setVote("Go")} data-testid="gonogo-vote-go"
                          className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg border-2 text-sm font-semibold transition-all ${vote === "Go" ? "border-green-500 bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400" : "border-slate-200 dark:border-slate-600 text-slate-400 dark:text-slate-500 hover:border-green-300 dark:hover:border-green-600"}`}>
                          <ThumbsUp className="w-4 h-4" /> Go
                        </button>
                        <button onClick={() => setVote("No Go")} data-testid="gonogo-vote-nogo"
                          className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg border-2 text-sm font-semibold transition-all ${vote === "No Go" ? "border-red-500 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400" : "border-slate-200 dark:border-slate-600 text-slate-400 dark:text-slate-500 hover:border-red-300 dark:hover:border-red-600"}`}>
                          <ThumbsDown className="w-4 h-4" /> No Go
                        </button>
                      </div>
                    </div>
                  </div>
                  <Textarea placeholder="Comment (optional)" rows={2} value={comment} onChange={(e) => setComment(e.target.value)} data-testid="gonogo-comment" />
                  <Button onClick={submitVote} disabled={submitting || !selectedStakeholder || !vote} className="bg-[#0F172A] hover:bg-slate-800 text-white" data-testid="gonogo-submit-btn">
                    <Send className="w-4 h-4 mr-2" />{submitting ? "Submitting..." : "Submit Vote"}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
          )}

          {/* Vote cards */}
          <div className="flex flex-col gap-3" data-testid="gonogo-votes-list">
            {votes.map((v) => (
              <Card key={v.id} className={`border shadow-sm bg-white dark:bg-slate-800/60 border-l-4 ${v.vote === "Go" ? "border-l-green-500" : "border-l-red-500"} dark:border-slate-700`} data-testid={`gonogo-vote-${v.id}`}>
                <CardContent className="p-5">
                  {editingId === v.id ? (
                    <div className="space-y-3" data-testid={`gonogo-edit-form-${v.id}`}>
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">Editing: {v.stakeholder_name}</p>
                        <button onClick={cancelEdit} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors" data-testid="gonogo-edit-cancel">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="flex gap-3">
                        <button onClick={() => setEditVote("Go")} data-testid="gonogo-edit-vote-go"
                          className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg border-2 text-sm font-semibold transition-all ${editVote === "Go" ? "border-green-500 bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400" : "border-slate-200 dark:border-slate-600 text-slate-400 dark:text-slate-500 hover:border-green-300 dark:hover:border-green-600"}`}>
                          <ThumbsUp className="w-4 h-4" /> Go
                        </button>
                        <button onClick={() => setEditVote("No Go")} data-testid="gonogo-edit-vote-nogo"
                          className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg border-2 text-sm font-semibold transition-all ${editVote === "No Go" ? "border-red-500 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400" : "border-slate-200 dark:border-slate-600 text-slate-400 dark:text-slate-500 hover:border-red-300 dark:hover:border-red-600"}`}>
                          <ThumbsDown className="w-4 h-4" /> No Go
                        </button>
                      </div>
                      <Textarea placeholder="Comment (optional)" rows={2} value={editComment} onChange={(e) => setEditComment(e.target.value)} data-testid="gonogo-edit-comment" />
                      <div className="flex gap-2">
                        <Button onClick={() => saveEdit(v.id)} disabled={saving || !editVote} size="sm" className="bg-[#0F172A] hover:bg-slate-800 text-white" data-testid="gonogo-edit-save">
                          <Check className="w-4 h-4 mr-1" />{saving ? "Saving..." : "Save"}
                        </Button>
                        <Button onClick={cancelEdit} variant="outline" size="sm" data-testid="gonogo-edit-cancel-btn">Cancel</Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-9 h-9 rounded-full flex items-center justify-center ${v.vote === "Go" ? "bg-green-50 dark:bg-green-900/30" : "bg-red-50 dark:bg-red-900/30"}`}>
                          {v.vote === "Go" ? <ThumbsUp className="w-4 h-4 text-green-600 dark:text-green-400" /> : <ThumbsDown className="w-4 h-4 text-red-600 dark:text-red-400" />}
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">{v.stakeholder_name}</p>
                          {v.comment && <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{v.comment}</p>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button onClick={() => startEdit(v)} className="p-1.5 rounded-md text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-all" data-testid={`gonogo-edit-btn-${v.id}`}>
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => setDeleteTarget({ id: v.id, label: `${v.stakeholder_name}'s vote` })} className="p-1.5 rounded-md text-slate-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 transition-all" data-testid={`gonogo-delete-btn-${v.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                        <Badge className={`${v.vote === "Go" ? "bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800" : "bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800"} border font-semibold`}>{v.vote}</Badge>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          {!showForm && (
            <div className="mt-6">
              <Button onClick={() => setShowForm(true)} className="bg-[#00D4C8] hover:bg-[#00B8AE] text-white font-semibold" data-testid="add-gonogo-btn">
                <Send className="w-4 h-4 mr-2" /> Add Go/No Go
              </Button>
            </div>
          )}
        </>
      )}
    </div>
    <ConfirmDeleteDialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} onConfirm={() => deleteVote(deleteTarget?.id)} title="Delete Vote" description={`Are you sure you want to delete ${deleteTarget?.label}? This action cannot be undone.`} />
    </>
  );
};
